/**
 * Deterministic chat-vs-dialog assignment for the cancellation flow A/B. The
 * rollout percentage comes from the backend at runtime (cancel-chat-config);
 * bucketing happens here, keyed on a stable customer identifier, so a customer
 * sees the same variant on every visit and the split can be rolled forward or
 * back (down to 0) without rebuilding the widget.
 *
 * The assignment is intent-to-treat: stamp it on tracking events as assigned,
 * even when a chat fatal error later falls the customer back to the dialog.
 * Both variants are always stamped explicitly — no fallback-to-control default
 * that could silently skew the split (the reviews A/B lesson).
 */

import type { CancelFlowVariant } from "@/lib/tracking";

export type { CancelFlowVariant };

/** FNV-1a over the key, folded to a stable bucket in [0, 100). */
export function hashToBucket(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

/**
 * Known limitation: the key is whichever identifier the session carries, in
 * priority order (customer id → email → buy-again token → device id; see
 * resolveCancelFlowKey), so a customer whose identifier AVAILABILITY changes
 * between visits can hash into a different bucket. In practice each source is
 * stable (the device id persists per browser), so this is accepted rather than
 * worked around client-side.
 */
export function assignCancelFlow(
  key: string | null | undefined,
  percentage: number,
): CancelFlowVariant {
  // No stable identifier → no stable assignment; never flip a customer's
  // experience between visits and never A/B an unidentifiable session.
  if (!key) return "dialog";
  if (percentage <= 0) return "dialog";
  if (percentage >= 100) return "chat";
  return hashToBucket(key) < percentage ? "chat" : "dialog";
}

/**
 * The stable key to bucket a session on, in priority order: customer id, then
 * email, then the buy-again token (email-link visitors), then the device id as
 * a universal fallback. Keeping the id/email formats raw preserves the buckets
 * of customers already in the A/B; the token/device fallbacks pull in the
 * sessions that previously carried no key and were all defaulted to the dialog
 * — the reason real chat coverage sat far below the configured percentage. The
 * device id is stable per browser, so the arm still can't flip within a device.
 */
export function resolveCancelFlowKey(sources: {
  shopifyCustomerId?: number | string | null;
  customerEmail?: string | null;
  buyAgainToken?: string | null;
  deviceId?: string | null;
}): string | null {
  const { shopifyCustomerId, customerEmail, buyAgainToken, deviceId } = sources;
  if (shopifyCustomerId != null && String(shopifyCustomerId) !== "") {
    return String(shopifyCustomerId);
  }
  if (customerEmail) return customerEmail;
  if (buyAgainToken) return buyAgainToken;
  if (deviceId) return deviceId;
  return null;
}

const ASSIGNMENT_STORAGE_KEY = "so_cancel_flow_assignment";

/**
 * The arm already assigned earlier in this tab session, if any. Lets the sheet
 * resolve the variant synchronously on later opens — before (or without) the
 * rollout-config fetch — so the surface can never flip between two opens.
 */
export function readStoredCancelFlowAssignment(): CancelFlowVariant | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(ASSIGNMENT_STORAGE_KEY);
    return stored === "chat" || stored === "dialog" ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Assignment with tab-session stickiness: the first resolved assignment wins
 * for the rest of the session, even if the rollout percentage moves. Nothing
 * is persisted for unidentifiable sessions — their "dialog" is a safe default,
 * not an assignment, and must not stick to a later identified visit.
 */
export function getStickyCancelFlowAssignment(
  key: string | null | undefined,
  percentage: number,
): CancelFlowVariant {
  const stored = readStoredCancelFlowAssignment();
  if (stored) return stored;
  const assigned = assignCancelFlow(key, percentage);
  if (key) {
    try {
      sessionStorage.setItem(ASSIGNMENT_STORAGE_KEY, assigned);
    } catch {
      // Storage unavailable → assignment just recomputes (deterministically).
    }
  }
  return assigned;
}
